import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as subscriptionsAPI from '@/api/subscriptions';

const initialState = {
  currentSubscription: null,
  allPlans: [],
  loading: false,
  error: null,
  checkoutLoading: false,
  portalLoading: false,
  changeLoading: false,
  changeError: null,
  lastPlanChange: null,
  events: [],
  eventsLoading: false,
  stripeStatus: null,
  stripeStatusLoading: false,
};

/**
 * Fetch current subscription for the active tenant
 */
export const fetchCurrentSubscription = createAsyncThunk(
  'subscriptions/fetchCurrent',
  async () => {
    return await subscriptionsAPI.getCurrentSubscription();
  }
);

/**
 * Fetch all available subscription plans
 */
export const fetchAllPlans = createAsyncThunk(
  'subscriptions/fetchAllPlans',
  async () => {
    return await subscriptionsAPI.getAllPlans();
  }
);

/**
 * Assign subscription plan to tenant (Super Admin only)
 */
export const assignPlan = createAsyncThunk(
  'subscriptions/assignPlan',
  async (data) => {
    return await subscriptionsAPI.assignPlan(data);
  }
);

/**
 * Create Stripe Checkout session and redirect to Stripe
 */
export const initiateCheckout = createAsyncThunk(
  'subscriptions/initiateCheckout',
  async ({ planId, billingCycle }) => {
    const result = await subscriptionsAPI.createCheckoutSession({
      plan_id: planId,
      billing_cycle: billingCycle,
    });
    window.location.href = result.checkout_url;
    return result;
  }
);

/**
 * Move to a different plan in place, without leaving the app.
 *
 * Checkout is a redirect and starts a *new* subscription; this modifies the
 * existing one, so the user keeps whatever they were doing and is not billed
 * twice. Requires a live subscription - a trialling tenant still needs
 * checkout.
 */
export const changePlan = createAsyncThunk(
  'subscriptions/changePlan',
  async ({ planId, billingCycle }, { dispatch, rejectWithValue }) => {
    try {
      const result = await subscriptionsAPI.changePlan({
        plan_id: planId,
        billing_cycle: billingCycle,
      });
      // An upgrade lifts the limits immediately, so re-read the subscription:
      // every usage bar and limit gate on the page is driven by it, and the
      // whole point is that the user can retry what they were blocked on.
      await dispatch(fetchCurrentSubscription());
      return result;
    } catch (error) {
      const body = error.response?.data;
      return rejectWithValue({
        code: body?.error || 'change_failed',
        // The API writes these messages for the user; the axios message never
        // is, so it is not offered as a fallback.
        message:
          body?.message ||
          'We could not change your plan. Please try again, or contact support if it keeps happening.',
        status: error.response?.status ?? null,
      });
    }
  }
);

/**
 * Create Stripe Portal session and redirect to Stripe
 */
export const openBillingPortal = createAsyncThunk(
  'subscriptions/openBillingPortal',
  async () => {
    const result = await subscriptionsAPI.createPortalSession();
    window.location.href = result.portal_url;
    return result;
  }
);

/**
 * Fetch subscription activity events
 */
export const fetchSubscriptionEvents = createAsyncThunk(
  'subscriptions/fetchEvents',
  async () => {
    return await subscriptionsAPI.getSubscriptionEvents();
  }
);

/**
 * Verify subscription status with Stripe
 */
export const verifyStripeStatus = createAsyncThunk(
  'subscriptions/verifyStripeStatus',
  async () => {
    return await subscriptionsAPI.verifySubscriptionStatus();
  }
);

const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    /**
     * Clear error state
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Clear the last plan-change attempt (error and result), so reopening the
     * dialog does not show the outcome of the previous one.
     */
    clearPlanChange: (state) => {
      state.changeError = null;
      state.lastPlanChange = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch current subscription
      .addCase(fetchCurrentSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSubscription = action.payload;
      })
      .addCase(fetchCurrentSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load subscription';
      })

      // Fetch all plans
      .addCase(fetchAllPlans.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchAllPlans.fulfilled, (state, action) => {
        state.allPlans = action.payload;
      })
      .addCase(fetchAllPlans.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to load plans';
      })

      // Assign plan
      .addCase(assignPlan.pending, (state) => {
        state.error = null;
      })
      .addCase(assignPlan.fulfilled, (state, action) => {
        // Update current subscription if assignment was for current tenant
        state.currentSubscription = action.payload;
      })
      .addCase(assignPlan.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to assign plan';
      })

      // Stripe Checkout
      .addCase(initiateCheckout.pending, (state) => {
        state.checkoutLoading = true;
        state.error = null;
      })
      .addCase(initiateCheckout.fulfilled, (state) => {
        state.checkoutLoading = false;
      })
      .addCase(initiateCheckout.rejected, (state, action) => {
        state.checkoutLoading = false;
        state.error = action.error.message || 'Failed to start checkout';
      })

      // Stripe Portal
      .addCase(openBillingPortal.pending, (state) => {
        state.portalLoading = true;
        state.error = null;
      })
      .addCase(openBillingPortal.fulfilled, (state) => {
        state.portalLoading = false;
      })
      .addCase(openBillingPortal.rejected, (state, action) => {
        state.portalLoading = false;
        state.error = action.error.message || 'Failed to open billing portal';
      })

      // In-app plan change
      .addCase(changePlan.pending, (state) => {
        state.changeLoading = true;
        state.changeError = null;
      })
      .addCase(changePlan.fulfilled, (state, action) => {
        state.changeLoading = false;
        state.lastPlanChange = action.payload;
      })
      .addCase(changePlan.rejected, (state, action) => {
        state.changeLoading = false;
        state.changeError = action.payload || {
          code: 'change_failed',
          message: 'We could not change your plan. Please try again.',
          status: null,
        };
      })

      // Subscription Events
      .addCase(fetchSubscriptionEvents.pending, (state) => {
        state.eventsLoading = true;
      })
      .addCase(fetchSubscriptionEvents.fulfilled, (state, action) => {
        state.eventsLoading = false;
        state.events = action.payload;
      })
      .addCase(fetchSubscriptionEvents.rejected, (state) => {
        state.eventsLoading = false;
      })

      // Verify Stripe Status
      .addCase(verifyStripeStatus.pending, (state) => {
        state.stripeStatusLoading = true;
      })
      .addCase(verifyStripeStatus.fulfilled, (state, action) => {
        state.stripeStatusLoading = false;
        state.stripeStatus = action.payload;
      })
      .addCase(verifyStripeStatus.rejected, (state) => {
        state.stripeStatusLoading = false;
      });
  },
});

export const { clearError, clearPlanChange } = subscriptionsSlice.actions;

// Selectors
// Null-safe: plan-limit notices render on pages whose tests mount only a few slices.
export const selectCurrentSubscription = (state) => state.subscriptions?.currentSubscription;
export const selectCurrentPlan = (state) => state.subscriptions.currentSubscription?.plan;
export const selectUsageStatistics = (state) => state.subscriptions?.currentSubscription?.usage;
export const selectAllPlans = (state) => state.subscriptions.allPlans;
export const selectSubscriptionsLoading = (state) => state.subscriptions.loading;
export const selectSubscriptionsError = (state) => state.subscriptions.error;

// Stripe-related selectors
export const selectCheckoutLoading = (state) => state.subscriptions.checkoutLoading;
export const selectPortalLoading = (state) => state.subscriptions.portalLoading;
export const selectPlanChangeLoading = (state) => state.subscriptions.changeLoading;
export const selectPlanChangeError = (state) => state.subscriptions.changeError;
export const selectLastPlanChange = (state) => state.subscriptions.lastPlanChange;

/**
 * True when the tenant has a subscription Stripe can modify in place. Without
 * one there is nothing to change, so the flow has to go through checkout.
 */
export const selectHasLiveSubscription = (state) =>
  Boolean(state.subscriptions.currentSubscription?.stripe_subscription_id);

// Events and Stripe status selectors
export const selectSubscriptionEvents = (state) => state.subscriptions.events;
export const selectEventsLoading = (state) => state.subscriptions.eventsLoading;
export const selectStripeStatus = (state) => state.subscriptions.stripeStatus;
export const selectStripeStatusLoading = (state) => state.subscriptions.stripeStatusLoading;

// Trial-related selectors
export const selectIsTrial = (state) => state.subscriptions.currentSubscription?.is_trial ?? false;
export const selectIsTrialExpired = (state) => state.subscriptions.currentSubscription?.is_expired ?? false;
export const selectTrialDaysRemaining = (state) => state.subscriptions.currentSubscription?.days_remaining;
export const selectTrialStatus = (state) => state.subscriptions.currentSubscription?.trial_status;

export default subscriptionsSlice.reducer;
